/**
 * lib/medicalDecisionTemplates.js
 *
 * Phase 2 — Templates de texte pré-rempli pour les missions secrétariat
 * générées par les décisions médicales. Tout mappé sur type=EXECUTE_CAT.
 *
 * Le médecin peut éditer le texte dans MedicalDecisionTaskModal avant
 * confirmation.
 */

export const DECISION_SOURCES = {
  ALERT_RESOLVE_HOSPITALIZATION: "ALERT_RESOLVE_HOSPITALIZATION",
  ALERT_VALIDATE_HOSPITALIZATION: "ALERT_VALIDATE_HOSPITALIZATION",
  ALERT_VALIDATE_TREATMENT_CHANGE: "ALERT_VALIDATE_TREATMENT_CHANGE",
  HDJ_SEVERE_PROBLEM: "HDJ_SEVERE_PROBLEM",
  HDJ_SAMEDAY_LAB: "HDJ_SAMEDAY_LAB",
  EVENT_HOSPITALIZATION: "EVENT_HOSPITALIZATION",
  EVENT_ER_VISIT: "EVENT_ER_VISIT",
  EVENT_TOXICITY_HIGH_GRADE: "EVENT_TOXICITY_HIGH_GRADE",
  EVENT_DISCHARGE: "EVENT_DISCHARGE",
};

// Titres lisibles pour l'UI de la modale
export const DECISION_TITLES = {
  ALERT_RESOLVE_HOSPITALIZATION: "Hospitalisation décidée (alerte)",
  ALERT_VALIDATE_HOSPITALIZATION: "Hospitalisation validée (alerte)",
  ALERT_VALIDATE_TREATMENT_CHANGE: "Modification de traitement",
  HDJ_SEVERE_PROBLEM: "Problème sévère en HDJ",
  HDJ_SAMEDAY_LAB: "Bilan du jour requis",
  EVENT_HOSPITALIZATION: "Hospitalisation enregistrée",
  EVENT_ER_VISIT: "Passage aux urgences",
  EVENT_TOXICITY_HIGH_GRADE: "Toxicité grade ≥ 3",
  EVENT_DISCHARGE: "Sortie d'hospitalisation",
};

/**
 * Construit le texte pré-rempli + priorité à partir du contexte.
 * @param {string} decisionSource — clé dans DECISION_SOURCES
 * @param {object} ctx — { patientName, doctorName, reason, description, severity, grade }
 * @returns {{ instructions: string, priority: 'URGENT'|'NORMAL'|'LOW' }}
 */
export function buildProposedTask(decisionSource, ctx = {}) {
  const p = ctx.patientName || "le patient";
  const who = ctx.doctorName ? `Dr ${ctx.doctorName}` : "le médecin";
  const reason = ctx.reason || ctx.description || "";

  switch (decisionSource) {
    case DECISION_SOURCES.ALERT_RESOLVE_HOSPITALIZATION:
    case DECISION_SOURCES.ALERT_VALIDATE_HOSPITALIZATION:
      return {
        priority: "URGENT",
        instructions:
          `${who} a décidé l'hospitalisation de ${p}` +
          (reason ? ` (motif : ${reason})` : "") +
          `. Merci d'organiser l'admission au plus tôt.`,
      };

    case DECISION_SOURCES.ALERT_VALIDATE_TREATMENT_CHANGE:
      return {
        priority: "NORMAL",
        instructions:
          `Modification du traitement décidée par ${who} pour ${p}` +
          (reason ? ` (${reason})` : "") +
          `. Merci de recontacter le patient pour expliquer + coordonner pharmacie si besoin.`,
      };

    case DECISION_SOURCES.HDJ_SEVERE_PROBLEM:
      return {
        priority: "URGENT",
        instructions:
          `Problème sévère signalé en HDJ pour ${p}` +
          (reason ? ` : ${reason}` : "") +
          `. Vérifier dans 24h que le patient est bien suivi médicalement.`,
      };

    case DECISION_SOURCES.HDJ_SAMEDAY_LAB:
      return {
        priority: "URGENT",
        instructions:
          `Bilan biologique requis le jour-J pour ${p} avant validation chimio. ` +
          `Contacter le labo pour passage urgent et transmettre les résultats à l'équipe HDJ.`,
      };

    case DECISION_SOURCES.EVENT_HOSPITALIZATION:
      return {
        priority: "URGENT",
        instructions:
          `Hospitalisation déclarée pour ${p}` +
          (reason ? ` (motif : ${reason})` : "") +
          `. Organiser l'admission et prévenir les référents.`,
      };

    case DECISION_SOURCES.EVENT_ER_VISIT:
      return {
        priority: "NORMAL",
        instructions:
          `${p} est passé aux urgences` +
          (reason ? ` (motif : ${reason})` : "") +
          `. Vérifier qu'un RDV de suivi est prévu et rappeler le patient.`,
      };

    case DECISION_SOURCES.EVENT_TOXICITY_HIGH_GRADE: {
      const g = ctx.grade ? `grade ${ctx.grade}` : "grade ≥ 3";
      return {
        priority: "URGENT",
        instructions:
          `Toxicité ${g} déclarée pour ${p}` +
          (reason ? ` (${reason})` : "") +
          `. Vérifier la prise en charge médicale et organiser un RDV de contrôle sous 48h.`,
      };
    }

    case DECISION_SOURCES.EVENT_DISCHARGE:
      return {
        priority: "NORMAL",
        instructions:
          `${p} sort d'hospitalisation. Reprendre contact pour planifier un RDV de contrôle ` +
          `et ajuster si besoin le calendrier chimio.`,
      };

    default:
      return {
        priority: "NORMAL",
        instructions:
          `Décision médicale concernant ${p}` +
          (reason ? ` : ${reason}` : "") +
          `. Merci de coordonner la suite.`,
      };
  }
}

export const PRIORITY_META = {
  URGENT: {
    label: "Urgent",
    color: "red",
    description: "À traiter dans la journée",
  },
  NORMAL: {
    label: "Normal",
    color: "amber",
    description: "À traiter sous 48h",
  },
  LOW: {
    label: "Faible",
    color: "slate",
    description: "À traiter dans la semaine",
  },
};
